import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Quote } from 'lucide-react';
import { useContent } from '@/hooks/useContent';
import { useRTLArrow } from '@/hooks/useRTL';

gsap.registerPlugin(ScrollTrigger);

const Testimonials = () => {
  const { t } = useContent();
  const testimonials = (t('home.testimonials.items', { returnObjects: true }) as Array<{
    id: number;
    name: string;
    content: string;
    rating: number;
  }>) || [];
  const { getChevron, isRTL } = useRTLArrow();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const PrevChevron = getChevron('left');
  const NextChevron = getChevron('right');
  
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    gsap.fromTo(
      section.querySelectorAll('.testimonial-card'),
      {
        opacity: 0,
        scale: 0.9,
      },
      {
        opacity: 1,
        scale: 1,
        duration: 0.6,
        stagger: 0.1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: section,
          start: 'top 80%',
        },
      }
    );
  }, [testimonials.length]);

  // Safety check: if no testimonials, don't render
  if (!testimonials || testimonials.length === 0) {
    return null;
  }

  const nextTestimonial = () => {
    if (testimonials.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    if (testimonials.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };
  
  // Ensure currentIndex is valid
  const currentTestimonial = testimonials[currentIndex] || testimonials[0];

  // In RTL, swap the handlers: left button goes next, right button goes previous
  const handleLeftClick = isRTL ? nextTestimonial : prevTestimonial;
  const handleRightClick = isRTL ? prevTestimonial : nextTestimonial;

  return (
    <section
      ref={sectionRef}
      className="py-20 bg-light-secondary dark:bg-dark-secondary"
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-light-text dark:text-dark-text">
            {t('home.testimonials.title')}
          </h2>
        </div>

        <div className="relative max-w-4xl mx-auto">
          <div className="testimonial-card relative p-8 bg-light-primary dark:bg-dark-primary rounded-xl shadow-lg">
            <Quote
              size={48}
              className="text-brand-purple-base dark:text-brand-orange-base mb-4"
            />
            <p className="text-lg text-light-text-secondary dark:text-dark-text-secondary mb-6">
              {currentTestimonial.content}
            </p>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-light-text dark:text-dark-text">
                  {currentTestimonial.name}
                </h3>
                <p className="text-light-text-secondary dark:text-dark-text-secondary">
                  {t('home.testimonials.patient')}
                </p>
              </div>
              <div className="flex gap-1">
                {[...Array(currentTestimonial.rating)].map((_, i) => (
                  <span key={i} className="text-brand-orange-base">
                    ★
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-center gap-4 mt-8">
            <button
              onClick={handleLeftClick}
              className="p-2 rounded-full bg-light-primary dark:bg-dark-primary hover:bg-light-accent dark:hover:bg-dark-accent transition-colors"
              aria-label={isRTL ? 'Next' : 'Previous'}
            >
              <PrevChevron size={24} />
            </button>
            <button
              onClick={handleRightClick}
              className="p-2 rounded-full bg-light-primary dark:bg-dark-primary hover:bg-light-accent dark:hover:bg-dark-accent transition-colors"
              aria-label={isRTL ? 'Previous' : 'Next'}
            >
              <NextChevron size={24} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;

